import { FC, useEffect, useRef } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { AppHeaderOverrideState } from '../AppHeaderOverrideState';
import { IonHeader, IonTitle, IonToolbar } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { NavigationMenuToggle } from '../navigation/NavigationMenu';
import { HeaderTabs, IHeaderTabGroup } from '../HeaderTabs';
import { SIDE_NAV_WIDTH } from '../navigation/NavConstants';
import { BeaSonarButton } from '../helper-bee/BeaSonarButton';
import { mediaMinWidth } from '../theme/utils';

const useStyles = createUseStyles({
    header: {
        borderBottom: 'var(--app-theme-border)',
        position: 'relative',
        background: 'var(--ion-color-primary)',

        // No toolbar colour: a coloured toolbar paints its own background over the band.
        '& > ion-toolbar': {
            '--color': 'white',
            '--background': 'transparent',
            '--border-width': 0
        }
    },
    title: {
        textAlign: 'center',
        paddingRight: 70,

        [mediaMinWidth.lg]: {
            paddingLeft: SIDE_NAV_WIDTH + 20,
        }
    },
    beaButtonWrapper: {
        position: 'absolute',
        top: 'calc(env(safe-area-inset-top, 0px) + 22px)',
        right: 22,
        zIndex: 100
    }
});

export interface IBasicHeader {
    title?: React.ReactNode;
    children?: React.ReactNode;
    tabs?: IHeaderTabGroup;
    hidesBeaButton?: boolean;
}

export const BasicHeader: FC<IBasicHeader> = ({ title, children, tabs, hidesBeaButton }) => {
    const classes = useStyles();
    const headerRef = useRef<HTMLIonHeaderElement>(null);
    const headerState = useInstance(AppHeaderOverrideState);

    useEffect(() => {
        const header = headerRef.current;
        if (!header) {
            return undefined;
        }
        const observer = new ResizeObserver(() => headerState.setHeight(header.getBoundingClientRect().height));
        observer.observe(header);
        return () => {
            observer.disconnect();
            headerState.setHeight(0);
        };
    }, [headerState]);

    return (
        <IonHeader className={classes.header} ref={headerRef}>
            <IonToolbar style={{paddingLeft: 10, paddingRight: 10}}>
                <NavigationMenuToggle slot="start" />
                <IonTitle className={classes.title}>
                    {title}
                </IonTitle>
                {children}
            </IonToolbar>
            {tabs &&
                <HeaderTabs tabs={tabs} />
            }
            {tabs && !hidesBeaButton && (
                <div className={classes.beaButtonWrapper}>
                    <BeaSonarButton
                        trackingFrom="Header Button"
                        data-id="bea-header-button"
                    />
                </div>
            )}
        </IonHeader>
    );
};
